import React, { useState, useEffect } from 'react';
import { Form, Button, Modal, Badge, Alert } from 'react-bootstrap';
import PicklistApi from '../../api/PicklistApi';
import { FaCheck, FaTimes } from 'react-icons/fa';
import {
  picklistOptionMatchesValue,
  picklistOptionStorageKey,
  resolveMultipicklistSelectedKeys,
  resolvePicklistSelectValue,
} from '../../utils/picklistValueUtils';

const PicklistValueSelector = ({
  field,
  value,
  onChange,
  disabled = false,
  isMultiSelect = false
}) => {
  const [picklistValues, setPicklistValues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedValues, setSelectedValues] = useState([]);

  const picklistApi = new PicklistApi();

  useEffect(() => {
    if (field && (field.data_type === 'picklist' || field.data_type === 'multipicklist')) {
      loadPicklistValues();
    }
  }, [field]);

  useEffect(() => {
    if (!value) {
      setSelectedValues([]);
      return;
    }
    if (isMultiSelect) {
      setSelectedValues(resolveMultipicklistSelectedKeys(picklistValues, value));
    } else {
      const resolved = resolvePicklistSelectValue(picklistValues, value);
      setSelectedValues(resolved ? [resolved] : [value]);
    }
  }, [value, isMultiSelect, picklistValues]);

  const isPicklistValueSelected = (picklistValue) =>
    selectedValues.some((selected) =>
      picklistOptionMatchesValue(picklistValue, selected) ||
      picklistValuesEqualStorage(picklistValue, selected)
    );

  function picklistValuesEqualStorage(picklistValue, selected) {
    return picklistOptionStorageKey(picklistValue) === String(selected).trim();
  }

  const resolvePicklistLabel = (storedKey) => {
    const match = picklistValues.find(
      (pv) =>
        picklistOptionMatchesValue(pv, storedKey) ||
        picklistOptionStorageKey(pv) === String(storedKey).trim()
    );
    return match?.value_label || match?.label || storedKey;
  };

  const loadPicklistValues = async () => {
    if (!field?.id) return;

    setLoading(true);
    setError('');

    try {
      const response = await picklistApi.getActivePicklistValues(field.id);
      if (response.data.success) {
        setPicklistValues(response.data.data || []);
      } else {
        setError('Failed to load picklist values');
      }
    } catch (error) {
      console.error('Error loading picklist values:', error);
      setError('Failed to load picklist values: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleValueToggle = (valueKey) => {
    if (isMultiSelect) {
      setSelectedValues(prev => {
        if (prev.some((v) => picklistValuesEqual(v, valueKey))) {
          return prev.filter((v) => !picklistValuesEqual(v, valueKey));
        }
        return [...prev, valueKey];
      });
    } else {
      setSelectedValues([valueKey]);
    }
  };

  function picklistValuesEqual(a, b) {
    return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
  }

  const handleApply = () => {
    if (isMultiSelect) {
      // Join with semicolon for multipicklist
      const newValue = selectedValues.join(';');
      onChange(newValue);
    } else {
      // Single value for picklist
      const newValue = selectedValues[0] || '';
      onChange(newValue);
    }
    setShowModal(false);
  };

  const handleClear = () => {
    setSelectedValues([]);
    onChange('');
    setShowModal(false);
  };

  const getDisplayValue = () => {
    if (!value) return 'Select value...';

    if (isMultiSelect) {
      const values = resolveMultipicklistSelectedKeys(picklistValues, value);
      if (values.length === 0) return 'Select values...';
      if (values.length === 1) return resolvePicklistLabel(values[0]);
      return `${values.length} values selected`;
    }
    return resolvePicklistLabel(value);
  };

  const getSelectedBadges = () => {
    if (!value) return null;

    const values = isMultiSelect
      ? resolveMultipicklistSelectedKeys(picklistValues, value)
      : [resolvePicklistSelectValue(picklistValues, value) || value];

    return (
      <div className="mt-2">
        {values.map((val, index) => (
          <Badge key={index} bg="primary" className="me-1 mb-1">
            {resolvePicklistLabel(val)}
          </Badge>
        ))}
      </div>
    );
  };

  if (!field || (field.data_type !== 'picklist' && field.data_type !== 'multipicklist')) {
    return null;
  }

  return (
    <>
      <Form.Control
        type="text"
        value={getDisplayValue()}
        readOnly
        disabled={disabled}
        onClick={() => !disabled && setShowModal(true)}
        style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
        placeholder={isMultiSelect ? 'Select values...' : 'Select value...'}
      />

      {getSelectedBadges()}

      <Modal show={showModal} onHide={() => setShowModal(false)} size="md">
        <Modal.Header closeButton>
          <Modal.Title>
            Select {isMultiSelect ? 'Values' : 'Value'} for {field.field_label}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          {loading ? (
            <div className="text-center py-3">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="picklist-values-list">
              {picklistValues.length === 0 ? (
                <Alert variant="info">No picklist values available for this field.</Alert>
              ) : (
                picklistValues.map((picklistValue) => (
                  <div
                    key={picklistValue.id}
                    className={`picklist-value-item ${isPicklistValueSelected(picklistValue) ? 'selected' : ''}`}
                    onClick={() => handleValueToggle(picklistValue.value_key)}
                    style={{
                      padding: '8px 12px',
                      margin: '4px 0',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      backgroundColor: isPicklistValueSelected(picklistValue) ? '#e3f2fd' : 'white',
                      borderColor: isPicklistValueSelected(picklistValue) ? '#2196f3' : '#dee2e6'
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <span>{picklistValue.value_label}</span>
                      {isPicklistValueSelected(picklistValue) && (
                        <FaCheck className="text-primary" />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleClear}>
            Clear
          </Button>
          <Button variant="primary" onClick={handleApply}>
            Apply
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PicklistValueSelector;
